import { useResource } from "../useResource.js";

// Матриця профілактики ризиковості: 20 кодів податкової інформації (п. 8 критеріїв, Довідник ДПС № 195) і критерії
// платника 1–7. Де компанія зараз, що поліпшити, які дії ведуть до ризиковості. Дані — lib/compliance/risk-matrix.js
const ST = {
  bad: { label: "за межею", cls: "rm-bad" }, warn: { label: "наближаємось", cls: "rm-warn" }, unknown: { label: "УВАЖНО", cls: "rm-unknown" },
  ok: { label: "добре", cls: "rm-ok" }, na: { label: "не стосується", cls: "rm-na" }, info: { label: "довідково", cls: "rm-info" },
};
const ORDER = { bad: 0, warn: 1, unknown: 2, ok: 3, na: 4 };
const fmt = (v, unit) => (v == null ? "немає даних" : typeof v === "number" ? `${v.toLocaleString("uk-UA")}${unit ? ` ${unit}` : ""}` : String(v));

export default function RiskMatrix({ companyId, version }) {
  const r = useResource(`/api/companies/${companyId}/compliance/risk-matrix`, version);
  const d = r.data;
  if (r.pending) return <section className="card"><h3>Матриця ризиковості</h3><p className="muted" role="status">Рахуємо…</p></section>;
  if (!d) return <section className="card"><h3>Матриця ризиковості</h3><p className="error small">{r.error?.message || "Не вдалося порахувати"}</p></section>;
  const codes = [...d.codes].sort((a, b) => ORDER[a.status] - ORDER[b.status] || a.code.localeCompare(b.code));
  const s = d.summary;
  return (
    <section className="card risk-matrix" aria-labelledby="rm-h">
      <h3 id="rm-h">Матриця ризиковості</h3>
      <p className="rm-summary">
        {s.bad > 0 && <span className="rm-pill rm-bad"><b>{s.bad}</b> за межею</span>}
        {s.warn > 0 && <span className="rm-pill rm-warn"><b>{s.warn}</b> наближаємось</span>}
        <span className="rm-pill rm-ok"><b>{s.ok}</b> добре</span>
        {s.unknown > 0 && <span className="rm-pill rm-unknown"><b>{s.unknown}</b> УВАЖНО — перевірте за поясненням</span>}
        {s.na > 0 && <span className="rm-pill rm-na"><b>{s.na}</b> не стосується</span>}
      </p>
      {d.practice && <Practice p={d.practice} />}
      <h4 className="h4">Коди податкової інформації (п. 8)</h4>
      <ul className="rm-list">
        {codes.map((c) => (
          <li key={c.code} className={`rm-item ${ST[c.status].cls}`}>
            <details open={c.status === "bad"}>
              <summary>
                <span className="rm-code">{c.code}</span>
                <span className="rm-title"><b>{c.title}</b><span className="muted small"> · {c.group}</span><br /><span className="small">{c.now}</span></span>
                <span className={`rm-pill ${ST[c.status].cls}`}>{ST[c.status].label}</span>
              </summary>
              {c.rows && (
                <div className="table-wrap" tabIndex={0} role="region" aria-label={`Показники коду ${c.code}`}>
                  <table className="table-static rm-rows">
                    <thead><tr><th>Показник</th><th className="num">Зараз</th><th>Межа</th><th>Стан</th></tr></thead>
                    <tbody>{c.rows.map((x) => (
                      <tr key={x.key}>
                        <td className="wrap-cell">{x.label}{x.detail ? <div className="muted small">{x.detail}</div> : null}<div className="muted small">{x.kind === "ecab" ? "Орієнтир eCabinet · " : ""}{x.source}</div></td>
                        <td className="num">{fmt(x.value, x.unit)}</td>
                        <td className="wrap-cell">{x.target}</td>
                        <td><span className={`rm-pill ${ST[x.status].cls}`}>{ST[x.status].label}</span></td>
                      </tr>))}</tbody>
                  </table>
                </div>)}
              {c.improve?.length > 0 && <><p className="rm-sub">Що поліпшити</p><ul className="small">{c.improve.map((t, i) => <li key={i}>{t}</li>)}</ul></>}
              {c.triggers?.length > 0 && <><p className="rm-sub">Що призведе до ризиковості — уникайте</p><ul className="small rm-triggers">{c.triggers.map((t, i) => <li key={i}>{t}</li>)}</ul></>}
            </details>
          </li>))}
      </ul>
      <h4 className="h4">Критерії ризиковості платника (1–7)</h4>
      <ul className="rm-list">{d.payer.map((c) => (
        <li key={c.n} className={`rm-item ${ST[c.status].cls}`}>
          <details open={c.status === "bad"}>
            <summary><span className="rm-code">{c.n}</span><span className="rm-title"><b>{c.title}</b><br /><span className="small">{c.now}</span></span><span className={`rm-pill ${ST[c.status].cls}`}>{ST[c.status].label}</span></summary>
            {c.improve?.length > 0 && <ul className="small">{c.improve.map((t, i) => <li key={i}>{t}</li>)}</ul>}
          </details>
        </li>))}</ul>
    </section>
  );
}

// Що вимагає комісія ДПС на практиці (lib/compliance/dps-practice.js): те, за чим вона вибирає операції, — раніше за неї
function Practice({ p }) {
  const items = [...p.items].sort((a, b) => ORDER[a.status] - ORDER[b.status]);
  return (
    <div className="rm-practice">
      <h4 className="h4">Що побачить і попросить ДПС</h4>
      <p className="small muted">Так комісія ДПС на практиці перевіряє платника, перш ніж визнати ризиковим, і що вимагає для зняття статусу. Норм тут немає — це реальні вимоги, тому краще виправити заздалегідь.</p>
      <ul className="rm-list">{items.map((x) => (
        <li key={x.key} className={`rm-item ${ST[x.status].cls}`}>
          <details open={x.status === "bad"}>
            <summary><span className="rm-title"><b>{x.title}</b><br /><span className="small">{x.now}</span></span><span className={`rm-pill ${ST[x.status].cls}`}>{ST[x.status].label}</span></summary>
            {x.todo?.length > 0 && <ol className="small">{x.todo.map((t, i) => <li key={i}>{t}</li>)}</ol>}
          </details>
        </li>))}</ul>
      <details className="rm-pack">
        <summary><b>Пакет документів, який просить комісія</b> <span className="muted small">— зберіть заздалегідь</span></summary>
        <ol className="small">{p.pack.map((t, i) => <li key={i}>{t}</li>)}</ol>
      </details>
    </div>
  );
}
